import { CoreSchemaTargetDto, DataSchemaDto, DataSchemaReference, DataSchemaType, DataSourceType, ResultSetSchemaTargetDto, SchemaTargetDto, SchemaType, UserDatasetSchemaTargetDto } from 'src/app/services/api.services';
import { INamedElement, NamedElementCollection } from './Collections';
import { DataSchemaItemGroup } from './DataSchemaItemGroup';
import { DataSource } from './DataSource';
import { DataTable } from './DataTable';
import { ItemDisplayOptions } from './ItemDisplayOptions';
import { deepClone } from 'src/app/shared/clone/cloner-service';

export class DataSchema implements INamedElement {
  readonly itemGroups = new DataSchema.ItemGroupCollection(this);
  readonly items = new DataSchema.ItemLookup(this);

  constructor(
    public readonly source: DataSource,
    public readonly reference: DataSchemaReference) { }

  get model() { return this.source.model; }

  get name() { return this.reference.name; }

  displayName: string | null = null;

  get title() { return this.displayName ?? this.name; }


  get type() { return this.reference.type; }

  get isCore() { return this.type == DataSchemaType.Core; }
  get isVisualCustomFields() { return this.type == DataSchemaType.VisualCustomFields; }

  factTable: DataTable | null = null;

  supportsCurrencyConversion = false;

  itemDisplay = new ItemDisplayOptions({});
  
  setFromDto(dto: DataSchemaDto) {
    this.displayName = dto.displayName;

    this.factTable =
      dto.factTable
        ? this.source.tables.get(dto.factTable)
        : null;

    this.supportsCurrencyConversion = dto.supportsCurrencyConversion;

    this.itemDisplay = new ItemDisplayOptions(dto.itemDisplay);

    dto.itemGroups.forEach(x => this.itemGroups.add(x.name).setFromDto(x));
  }

  getSchemaTargetDto(): SchemaTargetDto {
    if (this.source.type == DataSourceType.Product) {
      return <CoreSchemaTargetDto>{
        type: SchemaType.Core,
        name: this.name,
        product: this.source.product.name
      };
    }

    if (this.source.type == DataSourceType.ResultSet) {
      return <ResultSetSchemaTargetDto>{
        type: SchemaType.ResultSet,
        name: this.name,
        resultSetId: this.source.resultSetId
      };
    }

    if (this.source.type == DataSourceType.UserDataset) {
      return <UserDatasetSchemaTargetDto>{
        type: SchemaType.UserDataset,
        name: this.name,
        userDatasetId: this.source.userDatasetId
      };
    }

    throw new Error(`Unsupported schema target type`, { cause: this });
  }

  toDto(): DataSchemaDto {
    return {
      displayName: this.displayName,
      factTable: this.factTable?.name,
      itemDisplay: deepClone(this.itemDisplay.dto),
      itemGroups: [...this.itemGroups.map(x => x.toDto())],
      reference: deepClone(this.reference),
      supportsCurrencyConversion: this.supportsCurrencyConversion,
    };
  }
}

export namespace DataSchema {
  export class ItemGroupCollection extends NamedElementCollection<DataSchemaItemGroup> {
    constructor(public readonly s: DataSchema) {
      super();
    }

    add(name: string) {
      let item = new DataSchemaItemGroup(this.s, name);
      this._add(item);
      return item;
    }

    remove(itemGroup: DataSchemaItemGroup) {
      return this._remove(itemGroup);
    }

    protected getBase() {
      return this.s.model.base?.schemas.find(this.s.source.reference, this.s.reference)?.itemGroups;
    }

    protected getExtensions() {
      return this.s.model.base?.extensions.map(
        x => x.schemas.find(this.s.source.reference, this.s.reference)?.itemGroups) ?? [];
    }
  }

  export class ItemLookup {
    constructor(public readonly s: DataSchema) { }

    find(group: string, ...path: string[]) {
      return this.s.itemGroups.find(group)?.items.find(...path);
    }

    get(group: string, ...path: string[]) {
      let item = this.find(group, ...path);
      if (!item)
        throw new Error(`Could not find schema item [${group}].[${path.join('].[')}]`);

      return item;
    }
  }
}
